import React, { useEffect } from "react";
import PageLayout from "lib/components/PageLayout";
import { GetStaticPaths, GetStaticProps } from "next";
import renderToString from "next-mdx-remote/render-to-string";
import hydrate from "next-mdx-remote/hydrate";
import { MdxRemote } from "next-mdx-remote/types";
import toc from "@jsdevtools/rehype-toc";
import slug from "rehype-slug";
import urls from "rehype-urls";
import link from "rehype-autolink-headings";
import sectionize from "remark-sectionize";
import Grid from "@material-ui/core/Grid";
import Wrapper from "lib/components/Wrapper";
import { useTheme } from "lib/contexts/themeContext";
import CardResource from "domain/resources/CardResource";
import stylesMDX from "domain/resources/stylesMDX";
import componentsMDX from "domain/resources/componentsMDX";
import useIsResMobile from "lib/hooks/useIsResMobile";
import Head from "lib/components/Head";
import ResourcesService from "domain/resources/resourcesService";

interface ContentPageProps {
  mdxSource: MdxRemote.Source;
  resourceMeta: ResourceMeta;
}

const ContentPage: React.FC<ContentPageProps> = ({
  mdxSource,
  resourceMeta,
}) => {
  // hydrate converted mdx content with custom components
  const content = hydrate(mdxSource, { ...componentsMDX });
  const theme = useTheme();

  /**
   * Relocate table of contents to its proper place in layout
   */
  useEffect(() => {
    const elemContainer = document.querySelector(".toc-container");
    const elemToc = document.querySelector(".content-container nav.toc");
    const elemTocNew = elemToc.cloneNode(true);
    /* only operate when the original nav has been re-populated
     * e.g. new resources has been loaded
     */
    if (elemToc.childElementCount > 0) {
      // reset the content of the toc elemContainer
      while (elemContainer.lastChild) {
        elemContainer.removeChild(elemContainer.lastChild);
      }
      // reset the content of the original toc
      while (elemToc.lastChild) {
        elemToc.removeChild(elemToc.lastChild);
      }
      // finally attach the toc to its proper place
      if (elemContainer && elemTocNew) {
        elemContainer.appendChild(elemTocNew);
      }
    }
  });

  /**
   * Table of contents highlight effect
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.querySelector("h2").getAttribute("id");
          if (entry.isIntersecting && entry.intersectionRect.height > 70) {
            document
              .querySelector(`nav li a[href="#${id}"]`)
              ?.parentElement.classList.add("active");
          } else {
            document
              .querySelector(`nav li a[href="#${id}"]`)
              ?.parentElement.classList.remove("active");
          }
        });
      },
      {
        threshold: [0.1, 0.5, 0.9],
      },
    );

    // Track all sections that have an `id` applied in its h2 headings
    document.querySelectorAll("h2[id]").forEach((header) => {
      // target section which is the immediate parent of the h2 heading
      observer.observe(header.parentElement);
    });
    return () => observer.disconnect();
  });

  const isResMobile = useIsResMobile();

  return (
    <PageLayout>
      <Meta resourceMeta={resourceMeta} />
      <Wrapper>
        <div css={stylesMDX}>
          <Grid container spacing={10}>
            <Grid item xs={12} md={8} className="content-container">
              {content}
            </Grid>
            <Grid
              item
              md={4}
              className="toc-container"
              css={`
                ${isResMobile ? "display: none" : ""}
              `}
            />
          </Grid>
          {/* related contents */}
          <div
            css={`
              margin-top: 80px;
              border-top: 1px solid ${theme.palette.grey[100]};
              padding-top: 40px;
              margin-bottom: 80px;
            `}
          >
            {resourceMeta.relatedContents?.length > 0 && (
              <>
                <h5
                  css={`
                    font-weight: bold;
                    font-size: 20px;
                    line-height: 28px;
                    text-transform: uppercase;
                    margin: 0 0 40px;
                  `}
                >
                  Related Information
                </h5>
                <Grid container spacing={8}>
                  {resourceMeta.relatedContents.map((content) => (
                    <Grid item xs={isResMobile ? 12 : 4} key={content.title}>
                      <CardResource
                        title={content.title}
                        slug={content.slug}
                        imageUrl={content.mainImageUrl}
                        imageAlt={content.mainImageAlt}
                      />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </div>
        </div>
      </Wrapper>
    </PageLayout>
  );
};

export default ContentPage;

const Meta = ({ resourceMeta }: { resourceMeta: ResourceMeta }) => {
  const metaData = getMetaData(resourceMeta);

  return (
    <Head
      title={`${metaData.title} | Lawfully Immigration Resource`}
      desc={metaData.description}
      url={metaData.url}
      imageUrl={metaData.mainImageUrl}
    >
      <meta property="og:type" content="article" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "http://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Lawfully",
              item: `${process.env.NEXT_PUBLIC_HOMEPAGE_URL}`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Resources",
              item: `${process.env.NEXT_PUBLIC_HOMEPAGE_URL}/resources`,
            },
          ],
        })}
      </script>
      {metaData.faqs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: metaData.faqs.map((entry) => ({
              "@type": "Question",
              name: entry.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: entry.answer,
              },
            })),
          })}
        </script>
      )}
    </Head>
  );
};

const getMetaData = ({
  title,
  description,
  subtitle,
  slug,
  faqs,
  mainImageUrl,
}: ResourceMeta) => ({
  mainImageUrl,
  title,
  description,
  subtitle,
  url: `/resources/${slug}`,
  faqs,
});

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  const { resourceSlug } = params;
  const resourcesService = new ResourcesService();
  const data = await resourcesService.getResource({
    slug: resourceSlug as string,
    preview: !!preview,
  });

  if (!data) {
    return { notFound: true };
  }

  const { content, ...resourceMeta } = data;
  // render mdx content into html string
  const mdxSource = await renderToString(content, {
    ...componentsMDX,
    scope: {
      mainImageUrl: resourceMeta.mainImageUrl,
      mainImageAlt: resourceMeta.mainImageAlt,
    },
    mdxOptions: {
      remarkPlugins: [sectionize],
      rehypePlugins: [
        slug,
        [toc, { headings: ["h2"] }],
        [link, { behavior: "append" }],
        [
          urls,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore Settings type doesn't allow function types for now
          (url, node) => {
            if (url.host || url.path) {
              // non-null host means that it's not an internal fragment link (used by toc)
              // non-null path means that it's an internal link (to other pages)
              node.properties.target = "_blank";
              node.properties.rel = "noopener noreferrer";
            }
          },
        ],
      ],
    },
  });

  return {
    props: {
      mdxSource,
      resourceMeta,
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await new ResourcesService().getListResourceSlug();
  const slugsFiltered = slugs.filter((slug) => !!slug);
  const paths = slugsFiltered.map((resourceSlug) => ({
    params: { resourceSlug },
  }));

  return {
    paths,
    fallback: "blocking",
  };
};
